import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedConfigurationId, EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import { store } from '#app/store'
import { type ApiConfiguration, type FullApiConfiguration, isTg3ApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { loadNationalCodes } from '#features/NationalCodes/loadNationalCodes'
import { overrideSwitchBaseParameters } from '#pages/Configuration/utils/apiConfigurationHelpers'
import { createInitialSelection } from '#pages/Configuration/utils/utils'
import { verifyPartPeriod } from '#store/middlewares/configurationRequests/configurationCreationListener'
import {
  applyConfigurationBaseParameterChange,
  type ConfigurationVersion,
  type EcmConfigurationVersion,
  type LocalConfigurationId,
  type TruckConfiguration,
  takeSnapshot,
} from '#store/slices/configurationSlice.ts'
import type { EnhancedLanguage, EnhancedMarket } from '#types/EnhancedMarket'
import { dateToString, isValidDateString, isValidPricingDate, pricingDateToString, stringToDate } from '#utils/DateString'
import { minMaxDate } from '#utils/minMaxDate'

export async function addOneWeekPartPeriod(configuration: TruckConfiguration) {
  if (!isValidDateString(configuration.apiConfiguration.partPeriod)) {
    throw new Error('Part period is not valid')
  }
  const partPeriod = stringToDate(configuration.apiConfiguration.partPeriod)
  partPeriod.setDate(partPeriod.getDate() + 7)

  console.log('Adding one week to part period', configuration.uuid, configuration.apiConfiguration, partPeriod)

  await modifyBaseParameters(configuration, {
    partPeriod: dateToString(partPeriod),
  })
}

export async function switchMarket(configuration: TruckConfiguration, market: EnhancedMarket, language?: EnhancedLanguage) {
  const usedLanguage =
    language ?? market.languages?.find((language) => language.languageCode === configuration.apiConfiguration.orderLanguage) ?? market.languages?.[0]

  if (!usedLanguage) {
    throw new Error('No language found for market')
  }

  const overrides: Partial<ApiConfiguration> = {
    orderCountry: market.countryCode,
    orderLanguage: usedLanguage?.languageCode,
    language: usedLanguage.languageId,
    configurationType: market.configTypeId || undefined,
    vehicleType: market.vehicleTypeId,
    view: market.viewId,
    market: market.marketId,
    // try to fit the part period to the new market
    partPeriod: isValidDateString(configuration.apiConfiguration.partPeriod)
      ? dateToString(
          minMaxDate(
            stringToDate(configuration.apiConfiguration.partPeriod),
            isValidPricingDate(market.validPartPeriodStart) ? stringToDate(pricingDateToString(market.validPartPeriodStart)) : undefined,
            isValidPricingDate(market.validPartPeriodEnd) ? stringToDate(pricingDateToString(market.validPartPeriodEnd)) : undefined,
          ),
        )
      : undefined,
  }

  await modifyBaseParameters(configuration, overrides)
}

export class AbortError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CancelError'
  }
}

export async function modifyBaseParameters(
  configuration: TruckConfiguration,
  overrides: Partial<ApiConfiguration>,
  abortSignal?: AbortSignal,
): Promise<TruckConfiguration> {
  if (configuration.step.stage === 'PendingStep') {
    throw new Error('Configuration is still pending')
  }

  if (!configuration.uuid) {
    throw new Error('Configuration UUID is missing')
  }

  const { ecmParameters, apiConfiguration } = overrideSwitchBaseParameters(configuration.apiConfiguration, overrides)

  const response = await store
    .dispatch(
      enhancedTmsApiService.endpoints.putConfigurationByUuidSwitchBaseParameters.initiate({
        uuid: configuration.uuid,
        switchBaseParametersRequest: {
          ecmParameters,
        },
      }),
    )
    .unwrap()

  if (abortSignal?.aborted) {
    throw new AbortError('Aborted')
  }

  const { newUuid } = response

  if (!newUuid) {
    throw new Error('No new UUID returned')
  }

  if (newUuid === configuration.uuid) {
    throw new Error('Configuration UUID did not change')
  }

  if (isTg3ApiConfiguration(apiConfiguration)) {
    throw new Error('Switching base parameters is not supported for TG3 configurations')
  }

  if (isValidDateString(apiConfiguration.desiredDealerDate)) {
    await verifyPartPeriod(store.dispatch, apiConfiguration.desiredDealerDate, apiConfiguration as FullApiConfiguration)
  }
  // we lose the estimate flag when switching markets, as it only built in the MAN-Code Service
  apiConfiguration.desiredDealerDateIsEstimate = false

  const stepResponse = await store
    .dispatch(
      enhancedTmsApiService.endpoints.postConfigurationByUuidStepAndStep.initiate({
        uuid: newUuid as EnhancedConfigurationId,
        step: configuration.step.requested === 'GeneralStep' ? 1 : 2,
        configurationStepDataOptions: { returnCurrentConfiguration: true },
      }),
    )
    .unwrap()

  if (abortSignal?.aborted) {
    throw new AbortError('Aborted')
  }

  const marketData = await loadNationalCodes(apiConfiguration.nationalCodeMarketIdOverride ?? (ecmParameters.marketID as EnhancedMarketId))

  if (abortSignal?.aborted) {
    throw new AbortError('Aborted')
  }

  const newConfiguration: TruckConfiguration = {
    id: `MAIC-TMP-${newUuid}` as LocalConfigurationId,
    type: 'truck-configuration',
    uuid: newUuid as EnhancedConfigurationId,
    version: (configuration.version + 1) as ConfigurationVersion,
    ecmVersion: (configuration.ecmVersion + 1) as EcmConfigurationVersion,
    sortOrder: configuration.sortOrder,
    lastApiInteraction: Date.now(),
    title: configuration.title,
    marketDenomination: stepResponse.marketDenomination ?? '',
    callingApiConfiguration: apiConfiguration,
    apiConfiguration: apiConfiguration as FullApiConfiguration,
    step: configuration.step,
    productGroups: stepResponse.productGroups,
    viewHighlight: configuration.viewHighlight,
    sidepanel: configuration.sidepanel,
    checks: {},
    optionalEquipment: [],
    creation: {
      createdAt: Date.now(),
      loadingStep: 'done',
    },
    marketData,
    // -- reset start
    // this is really nasty, we have no more initialSelection or configuredFamilies for the new configuration
    snapshots: [],
    configuredFamilies: {},
    initialSelection: {},
    history: [],
    // -- reset end
  }

  newConfiguration.initialSelection = createInitialSelection(newConfiguration)

  return newConfiguration
}

export async function applyBaseParameterOverrides(
  baseConfiguration: TruckConfiguration,
  updatedConfiguration: TruckConfiguration,
  _abortSignal?: AbortSignal,
) {
  store.dispatch(
    applyConfigurationBaseParameterChange({
      id: baseConfiguration.id,
      overrideConfiguration: updatedConfiguration,
    }),
  )

  store.dispatch(
    takeSnapshot({
      id: baseConfiguration.id,
      view: updatedConfiguration.step.view,
      version: updatedConfiguration.version,
      productGroups: updatedConfiguration.productGroups,
      reason: 'current-state',
      initialSelection: updatedConfiguration.initialSelection,
      configuredFamilies: updatedConfiguration.configuredFamilies,
    }),
  )

  console.log('Updated configuration:', updatedConfiguration)
}
